import React, { useEffect, useState } from 'react';

export type ToastKind = 'info' | 'success' | 'warn' | 'error';

export interface ToastProps {
  id: string;
  kind: ToastKind;
  message: string;
  durationMs: number;
  onDismiss: (id: string) => void;
}

const KIND_CONFIG: Record<ToastKind, { icon: string; bg: string; text: string; role: 'status' | 'alert'; ariaLive: 'polite' | 'assertive' }> = {
  info:    { icon: 'ℹ', bg: 'bg-blue-500',   text: 'text-white', role: 'status', ariaLive: 'polite' },
  success: { icon: '✓', bg: 'bg-green-500',  text: 'text-white', role: 'status', ariaLive: 'polite' },
  warn:    { icon: '⚠', bg: 'bg-orange-500', text: 'text-white', role: 'alert',  ariaLive: 'assertive' },
  error:   { icon: '✕', bg: 'bg-red-500',    text: 'text-white', role: 'alert',  ariaLive: 'assertive' },
};

export const Toast: React.FC<ToastProps> = ({ id, kind, message, durationMs, onDismiss }) => {
  const { icon, bg, text, role, ariaLive } = KIND_CONFIG[kind];
  const [fadingOut, setFadingOut] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setFadingOut(true);
      onDismiss(id);
    }, durationMs);
    return () => window.clearTimeout(t);
  }, [id, durationMs, onDismiss]);

  return (
    <div
      role={role}
      aria-live={ariaLive}
      className={`pointer-events-auto flex items-start gap-2 px-4 py-2 rounded shadow-md min-w-[200px] max-w-sm transition-opacity duration-300 ${bg} ${text} ${fadingOut ? 'opacity-0' : 'opacity-100'}`}
    >
      <span data-testid="toast-icon" aria-hidden="true" className="text-base leading-6">
        {icon}
      </span>
      <span className="text-sm leading-6 break-words">{message}</span>
    </div>
  );
};
